import Logger from "../services/Logger";
import { isMobile } from "./isMobile";

export interface BaggageInfo {
  Weight?: string;
  // eslint-disable-next-line camelcase
  Weight_2?: string;
  Quantity?: string;
  // eslint-disable-next-line camelcase
  Quantity_2?: string;
  DetailsAvailable?: string;
  BaggageSelectionAvailable?: string;
  Included?: string;
  source: "search" | "priceActualization" | "url";
}

export interface BaggageLimit {
  Weight: string;
  // eslint-disable-next-line camelcase
  Weight_2?: string;
  Quantity: string;
  // eslint-disable-next-line camelcase
  Quantity_2?: string;
  DetailsAvailable: string;
  BaggageSelectionAvailable: string;
  Included?: string;
}

export const BAGGAGE_ITEM_TYPES = {
  CARRY_ON: "carry-on",
  CARRY_ON_SPI: "carry-on-spi",
} as const;

export function isCarryOnSpiBaggageType(type?: string): boolean {
  return type === BAGGAGE_ITEM_TYPES.CARRY_ON_SPI;
}

export function isCarryOnBaggageType(type?: string): boolean {
  return type === BAGGAGE_ITEM_TYPES.CARRY_ON || isCarryOnSpiBaggageType(type);
}

export function isCarryOnBaggageItem(item: { Type?: string }): boolean {
  return isCarryOnBaggageType(item.Type);
}

export function isCarryOnSpiBaggageItem(item: { Type?: string }): boolean {
  return isCarryOnSpiBaggageType(item.Type);
}

export function extractBaggageFromUrl(
  urlParams: URLSearchParams
): BaggageInfo | null {
  const detailsAvailable = urlParams.get("detailsAvailable");
  const quantity = urlParams.get("baggageLimitQuantity");
  const weight = urlParams.get("baggageLimitWeight");
  const included = urlParams.get("baggageIncluded");

  if (!detailsAvailable && !quantity && !weight && !included) {
    return null;
  }

  return {
    DetailsAvailable: detailsAvailable || "false",
    Quantity: quantity || "",
    Weight: weight || "",
    Included: included || "Unknown",
    source: "url",
  };
}

export function extractBaggageFromPriceActualization(
  priceActualizationResponse: any
): BaggageInfo | null {
  const flightOffer =
    priceActualizationResponse?.GolApi?.ResponseDetail
      ?.PriceActualizationResponse_2?.FlightOffer;
  const codeBook = priceActualizationResponse?.GolApi?.CodeBook;

  if (flightOffer?.BaggageLimit) {
    return {
      Weight: flightOffer.BaggageLimit.Weight || "",
      Weight_2: flightOffer.BaggageLimit.Weight_2,
      Quantity: flightOffer.BaggageLimit.Quantity || "",
      Quantity_2: flightOffer.BaggageLimit.Quantity_2,
      DetailsAvailable: flightOffer.BaggageLimit.DetailsAvailable || "true",
      BaggageSelectionAvailable:
        flightOffer.BaggageLimit.BaggageSelectionAvailable || "false",
      Included: flightOffer.BaggageLimit.Included || "Unknown",
      source: "priceActualization",
    };
  }

  const pricingDetail = flightOffer?.PricingDetails?.PricingDetail?.[0];
  if (pricingDetail?.BaggageLimit) {
    return {
      Weight: pricingDetail.BaggageLimit.Weight || "",
      Weight_2: pricingDetail.BaggageLimit.Weight_2,
      Quantity: pricingDetail.BaggageLimit.Quantity || "",
      Quantity_2: pricingDetail.BaggageLimit.Quantity_2,
      DetailsAvailable: pricingDetail.BaggageLimit.DetailsAvailable || "true",
      BaggageSelectionAvailable:
        pricingDetail.BaggageLimit.BaggageSelectionAvailable || "false",
      Included: pricingDetail.BaggageLimit.Included || "Unknown",
      source: "priceActualization",
    };
  }

  const brandServiceNames = codeBook?.BrandServiceNames?.BrandServiceName;
  if (brandServiceNames && Array.isArray(brandServiceNames)) {
    const hasCheckedBaggage = brandServiceNames.some(
      (service: any) => service.Type === "CheckedBaggage"
    );

    if (hasCheckedBaggage) {
      return {
        DetailsAvailable: "true",
        BaggageSelectionAvailable: "true",
        Included: "Unknown",
        source: "priceActualization",
      };
    }
  }

  return null;
}

export function getBestAvailableBaggageInfo(
  searchBaggage: BaggageInfo | null,
  priceActualizationBaggage: BaggageInfo | null,
  urlBaggage: BaggageInfo | null
): BaggageInfo | null {
  if (urlBaggage) {
    return urlBaggage;
  }

  const isSearchBaggageComplete =
    searchBaggage &&
    searchBaggage.Weight &&
    searchBaggage.Weight !== "0" &&
    searchBaggage.Quantity &&
    searchBaggage.Quantity !== "0" &&
    searchBaggage.Included !== "Unknown";

  if (isSearchBaggageComplete) {
    return searchBaggage;
  }

  if (priceActualizationBaggage) {
    return priceActualizationBaggage;
  }

  if (searchBaggage) {
    return searchBaggage;
  }

  return null;
}

export function extractBaggageFromSearchResults(
  searchState: any,
  refs: string[]
): BaggageInfo | null {
  if (!searchState?.oFlightOffers || !refs || refs.length === 0) {
    return null;
  }

  const flightOfferKeys = Object.keys(searchState.oFlightOffers);

  const matchingOffer = flightOfferKeys.find((flightOfferKey) => {
    const flightOffer = searchState.oFlightOffers[flightOfferKey];

    if (flightOffer?.FlightCombinations?.FlightCombination) {
      return flightOffer.FlightCombinations.FlightCombination.some(
        (combination) => {
          const combinationRefs =
            combination.References?.Reference?.map((ref: any) => ref.$t) || [];

          return refs.some((ref) => combinationRefs.includes(ref));
        }
      );
    }
    return false;
  });

  if (matchingOffer) {
    const flightOffer = searchState.oFlightOffers[matchingOffer];
    const matchingCombination = flightOffer.FlightCombinations.FlightCombination.find(
      (combination) => {
        const combinationRefs =
          combination.References?.Reference?.map((ref: any) => ref.$t) || [];
        return refs.some((ref) => combinationRefs.includes(ref));
      }
    );

    if (matchingCombination?.PricingDetail?.BaggageLimit) {
      const baggageLimit = matchingCombination.PricingDetail.BaggageLimit;
      return {
        Weight: baggageLimit.Weight || "",
        Weight_2: baggageLimit.Weight_2,
        Quantity: baggageLimit.Quantity || "",
        Quantity_2: baggageLimit.Quantity_2,
        DetailsAvailable: baggageLimit.DetailsAvailable || "false",
        BaggageSelectionAvailable:
          baggageLimit.BaggageSelectionAvailable || "false",
        Included: baggageLimit.Included || "Unknown",
        source: "search",
      };
    }
  }

  return null;
}

export function updateUrlWithBaggageInfo(baggageInfo: BaggageInfo): void {
  if (isMobile()) {
    return;
  }

  Logger.info("updateUrlWithBaggageInfo called with:", baggageInfo);

  const newUrl = new URL(window.location.href);

  if (baggageInfo.Weight !== undefined) {
    newUrl.searchParams.set("baggageLimitWeight", baggageInfo.Weight);
  }
  if (baggageInfo.Quantity !== undefined) {
    newUrl.searchParams.set("baggageLimitQuantity", baggageInfo.Quantity);
  }
  if (baggageInfo.Included !== undefined) {
    newUrl.searchParams.set("baggageIncluded", baggageInfo.Included);
  }
  if (baggageInfo.DetailsAvailable !== undefined) {
    newUrl.searchParams.set("detailsAvailable", baggageInfo.DetailsAvailable);
  }
  if (baggageInfo.BaggageSelectionAvailable !== undefined) {
    newUrl.searchParams.set(
      "baggageSelectionAvailable",
      baggageInfo.BaggageSelectionAvailable
    );
  }

  Logger.info("URL before update:", window.location.href);
  Logger.info("URL after update:", newUrl.toString());

  window.history.replaceState({}, "", newUrl.toString());
}

export function baggageInfoToBaggageLimit(
  baggageInfo: BaggageInfo | null
): BaggageLimit | null {
  if (!baggageInfo) {
    return null;
  }

  return {
    Weight: baggageInfo.Weight || "",
    Weight_2: baggageInfo.Weight_2,
    Quantity: baggageInfo.Quantity || "",
    Quantity_2: baggageInfo.Quantity_2,
    DetailsAvailable: baggageInfo.DetailsAvailable || "false",
    BaggageSelectionAvailable: baggageInfo.BaggageSelectionAvailable || "false",
    Included: baggageInfo.Included,
  };
}
